/** A new item's form: its title, its entity, whether it's private; then its page. */
/** "New": making an item, a document or a spreadsheet (the items list's, and the triage area's into it). */

import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemDetail, ObjEntity, SpaceOut } from "../../api/types.gen.ts";
import { locale, t } from "../../i18n/index.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { onPhone } from "../../lib/platform.ts";
import { useAction } from "../../lib/resource.ts";
import { location, navigate } from "../../lib/router.ts";
import { Button } from "../../ui/Button.tsx";
import { DialogActions } from "../../ui/Dialog.tsx";
import { Checkbox, Field, Form, Input } from "../../ui/form.tsx";
import { EntitySelect } from "./entitySelect.tsx";

/** What a new item is: one with fields (a record), or a document or spreadsheet made in Revisa. */
export type What = "record" | "doc" | "sheet";

/** Made in a triage area: into it, private to you (yours) or the organization's. */
export interface Into {
  mine: boolean;
}

/**
 * A new item, document or spreadsheet: its title (a document's may wait: it's "Untitled" until then), its entity (the
 * list's, if it shows one's), whether it's kept private (in a triage area, whose it is says so); then its page (a
 * document's with its editor open).
 */
export function NewItemForm(p: {
  space: SpaceOut;
  entities: readonly ObjEntity[];
  what: What;
  /** Picked with it, from the menu. */
  entity?: string | undefined;
  triage: Into | undefined;
  /** Made in this folder (none: the space's items, or the triage area). */
  folder: string | null;
  onCancel: () => void;
}) {
  const { what, triage } = p;
  const [title, setTitle] = useState("");
  const [entity, setEntity] = useState(
    () => p.entity ?? (location.value.searchParams.get("entity") ?? "").replace("none", ""),
  );
  const [kept, setKept] = useState(false);
  const mine = triage ? triage.mine : kept;
  const [create, busy] = useAction(async () => {
    const into = triage ? { triage: true } : p.folder ? { folder: p.folder } : {};
    if (what !== "record") {
      // A document in the language you write in (yours: its spell checking and numbers).
      const body = {
        kind: what,
        title: title || null,
        entity: entity || null,
        private: mine,
        lang: locale.value,
        // On a phone, a document's in free flow (no pages: as wide as its screen).
        flow: onPhone(),
        ...into,
      };
      const item = await api.post<ItemDetail>(spaceApi(p.space, "/files/new"), body);
      navigate(spaceUrl(p.space, `/items/${item.id}?edit=1`));
      return;
    }
    const body = { title, entity: entity || null, visibility: mine ? "private" : "org", ...into };
    const item = await api.post<ItemDetail>(spaceApi(p.space, "/items"), body);
    navigate(spaceUrl(p.space, `/items/${item.id}`));
  });
  return (
    <Form class="flex flex-col gap-4" onSubmit={create}>
      <Field label={t("items.title")}>
        <Input value={title} onValue={setTitle} required={what === "record"} autofocus={true} />
      </Field>
      <Field label={t("items.entity")}>
        <EntitySelect entities={p.entities} value={entity} onValue={setEntity} />
      </Field>
      {triage ? null : <Checkbox label={t("items.keepPrivate")} checked={kept} onCheck={setKept} />}
      <DialogActions>
        <Button answer="cancel" onClick={p.onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {t("common.create")}
        </Button>
      </DialogActions>
    </Form>
  );
}
